import React, { useState } from 'react';
import useDialog from '../hooks/useDialog';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import SocialSignIn from './SocialSignIn';
import { useTranslation } from '../contexts/LanguageContext';
import { localizedPath } from '../i18n/languages';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import '../css/auth.css';

const DocLink = ({ href, children }) => {
    const { language } = useTranslation();
    return <a href={localizedPath(href, language)} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>{children}</a>;
};

const LoginModal = ({ show, onClose, message = '', startInSignup = false }) => {
    const dialogRef = useDialog(show, onClose);
    const { t } = useTranslation();
    const { login, signup, socialLogin, checkAuth } = useAuth();
    const [isSignup, setIsSignup] = useState(startInSignup);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [tosAgreed, setTosAgreed] = useState(false);

    // Verification state
    const [verificationSent, setVerificationSent] = useState(false);
    const [verificationEmail, setVerificationEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    // Shown while the post-OAuth session is being established
    const [finalizing, setFinalizing] = useState(false);

    // Post-signup username setup (new social accounts only)
    const [usernameSetup, setUsernameSetup] = useState(false);
    const [setupName, setSetupName] = useState('');
    const [setupError, setSetupError] = useState('');
    const [setupLoading, setSetupLoading] = useState(false);

    useBodyScrollLock(show);

    if (!show) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResendSuccess(false);
        setLoading(true);

        if (isSignup) {
            if (password !== confirmPassword) {
                setError(t('auth.errors.passwordsMismatch'));
                setLoading(false);
                return;
            }
            if (password.length < 6) {
                setError(t('auth.errors.passwordTooShort'));
                setLoading(false);
                return;
            }

            const result = await signup(email, password, username);
            if (result.success && result.verification_required) {
                setVerificationSent(true);
                setVerificationEmail(result.email || email);
            } else if (!result.success) {
                if (result.can_resend) {
                    setVerificationSent(true);
                    setVerificationEmail(result.email || email);
                    setError(result.error);
                } else {
                    setError(result.error || t('auth.errors.signupFailed'));
                }
            }
        } else {
            const result = await login(email, password);
            if (!result.success) {
                if (result.email_unverified) {
                    setVerificationSent(true);
                    setVerificationEmail(result.email || email);
                    setError(result.error);
                } else {
                    setError(result.error || t('auth.errors.loginFailed'));
                }
            }
        }

        setLoading(false);
    };

    const handleResend = async () => {
        setResendLoading(true);
        setResendSuccess(false);
        setError('');
        try {
            const result = await api.resendVerification(verificationEmail);
            if (result.success) {
                setResendSuccess(true);
            } else {
                setError(result.error || t('auth.errors.resendFailed'));
            }
        } catch (err) {
            setError(err.message || t('auth.errors.resendFailed'));
        }
        setResendLoading(false);
    };

    const handleBackToLogin = () => {
        setVerificationSent(false);
        setVerificationEmail('');
        setError('');
        setResendSuccess(false);
        setIsSignup(false);
    };

    const toggleMode = () => {
        setIsSignup(!isSignup);
        setError('');
        setVerificationSent(false);
        setResendSuccess(false);
        setTosAgreed(false);
    };

    const handleSocialSuccess = async (user) => {
        // The user is already authenticated server-side; checkAuth() loads
        // the session and can take a moment — show a loading screen so the
        // bare login form isn't left sitting there.
        setFinalizing(true);
        try {
            await checkAuth();
            if (user && user.new_account) {
                setSetupName(user.username || '');
                setSetupError('');
                setUsernameSetup(true);
            } else {
                onClose();
            }
        } finally {
            setFinalizing(false);
        }
    };

    const handleSocialError = (errorMessage) => {
        setError(errorMessage);
    };

    const handleUsernameSetupSave = async (e) => {
        e.preventDefault();
        const name = setupName.trim();
        if (name.length < 3) {
            setSetupError(t('auth.errors.usernameTooShort'));
            return;
        }
        setSetupLoading(true);
        setSetupError('');
        try {
            const result = await api.updateProfile(name);
            if (result.success) {
                await checkAuth();
                onClose();
            } else {
                setSetupError(result.error || t('auth.errors.setUsernameFailed'));
            }
        } catch (err) {
            setSetupError(err.message || t('auth.errors.setUsernameFailed'));
        }
        setSetupLoading(false);
    };

    const handleUsernameSetupSkip = () => {
        // Already signed in with the auto-generated username; can rename later.
        onClose();
    };

    return (
        <div className="modal-overlay-base modal-overlay" onClick={onClose}>
            <div className="modal-content-base login-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose} aria-label={t('common.close')}>
                    <span aria-hidden="true">&times;</span>
                </button>

                {finalizing ? (
                    <div className="auth-finalizing">
                        <div className="auth-finalizing-spinner"></div>
                        <p>{t('auth.signingIn')}</p>
                    </div>
                ) : usernameSetup ? (
                    <>
                        <div className="modal-header">
                            <img src="/logo192.png" alt="Word Labyrinth" className="modal-logo" />
                            <h2>{t('auth.chooseUsername')}</h2>
                            <p className="modal-message">{t('auth.signedInPickUsername')}</p>
                        </div>

                        {setupError && (
                            <div className="error-message">
                                <i className="fa fa-exclamation-circle"></i>
                                {setupError}
                            </div>
                        )}

                        <form onSubmit={handleUsernameSetupSave} className="auth-form">
                            <div className="form-group">
                                <label htmlFor="setup-username">{t('auth.username')}</label>
                                <input
                                    type="text"
                                    id="setup-username"
                                    value={setupName}
                                    onChange={(e) => setSetupName(e.target.value)}
                                    placeholder={t('auth.usernamePlaceholder')}
                                    minLength={3}
                                    maxLength={30}
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                className="submit-button"
                                disabled={setupLoading}
                            >
                                {setupLoading ? (
                                    <span className="loading-spinner-small"></span>
                                ) : (
                                    t('auth.saveUsername')
                                )}
                            </button>
                        </form>

                        <button
                            type="button"
                            className="back-to-login-button"
                            onClick={handleUsernameSetupSkip}
                        >
                            {t('auth.skipForNow')}
                        </button>
                    </>
                ) : verificationSent ? (
                    <>
                        <div className="verification-notice">
                            <div className="verification-icon">
                                <i className="fa fa-envelope"></i>
                            </div>
                            <h3>{t('auth.verification.title')}</h3>
                            <p>{t.rich('auth.verification.sent', { b: <strong /> }, { email: verificationEmail })}</p>
                            <p className="verification-hint">{t('auth.verification.hint')}</p>
                        </div>

                        {error && (
                            <div className="error-message">
                                <i className="fa fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}

                        {resendSuccess && (
                            <div className="success-message">
                                <i className="fa fa-check-circle"></i>
                                {t('auth.verification.resent')}
                            </div>
                        )}

                        <button
                            className="resend-button"
                            onClick={handleResend}
                            disabled={resendLoading || resendSuccess}
                        >
                            {resendLoading ? (
                                <span className="loading-spinner-small"></span>
                            ) : resendSuccess ? (
                                t('auth.verification.emailSent')
                            ) : (
                                t('auth.verification.resend')
                            )}
                        </button>

                        <button
                            className="back-to-login-button"
                            onClick={handleBackToLogin}
                        >
                            {t('auth.verification.backToLogin')}
                        </button>
                    </>
                ) : (
                    <>
                        <div className="modal-header">
                            <img src="/logo192.png" alt="Word Labyrinth" className="modal-logo" />
                            <h2>{isSignup ? t('auth.createAccount') : t('auth.welcomeBack')}</h2>
                            {message && <p className="modal-message">{message}</p>}
                        </div>

                        {error && (
                            <div className="error-message">
                                <i className="fa fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="auth-form">
                            <div className="form-group">
                                <label htmlFor="email">{t('auth.email')}</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('auth.emailPlaceholder')}
                                    required
                                />
                            </div>

                            {isSignup && (
                                <div className="form-group">
                                    <label htmlFor="username">{t('auth.usernameOptional')}</label>
                                    <input
                                        type="text"
                                        id="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder={t('auth.usernamePlaceholder')}
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="password">{t('auth.password')}</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                />
                            </div>

                            {isSignup && (
                                <div className="form-group">
                                    <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                            )}

                            {isSignup && (
                                <div className="form-group" style={{ marginTop: '4px' }}>
                                    <label className="tos-consent-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', lineHeight: '1.4', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={tosAgreed}
                                            onChange={(e) => setTosAgreed(e.target.checked)}
                                            style={{ width: '18px', height: '18px', minWidth: '18px', marginTop: '1px', accentColor: '#667eea' }}
                                        />
                                        <span>
                                            {t.rich('auth.tosConsent', {
                                                terms: <DocLink href="/terms" />,
                                                privacy: <DocLink href="/privacy" />,
                                            })}
                                        </span>
                                    </label>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="submit-button"
                                disabled={loading || (isSignup && !tosAgreed)}
                            >
                                {loading ? (
                                    <span className="loading-spinner-small"></span>
                                ) : (
                                    isSignup ? t('auth.createAccount') : t('auth.logIn')
                                )}
                            </button>
                        </form>

                        <SocialSignIn
                            onSuccess={handleSocialSuccess}
                            onError={handleSocialError}
                        />

                        <div className="auth-switch">
                            {isSignup ? (
                                <p>
                                    {t('auth.haveAccount')}{' '}
                                    <button type="button" onClick={toggleMode}>
                                        {t('auth.logInLink')}
                                    </button>
                                </p>
                            ) : (
                                <p>
                                    {t('auth.noAccount')}{' '}
                                    <button type="button" onClick={toggleMode}>
                                        {t('auth.signUpLink')}
                                    </button>
                                </p>
                            )}
                        </div>

                        <div className="auth-benefits">
                            <h4>{t('auth.benefits.title')}</h4>
                            <ul>
                                <li><i className="fa fa-save"></i> {t('auth.benefits.saveProgress')}</li>
                                <li><i className="fa fa-trophy"></i> {t('auth.benefits.compete')}</li>
                                <li><i className="fa fa-chart-line"></i> {t('auth.benefits.trackStats')}</li>
                                <li><i className="fa fa-sync"></i> {t('auth.benefits.sync')}</li>
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginModal;
